import { useEffect } from "react";

// Absolute site origin used for canonical + OG URL construction.
const SITE_ORIGIN = "https://districtwellnesscenter.com";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.jpg`;

// ---------- helpers ----------
const setOrCreate = (selector, tag, attrs, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  if (content !== undefined) el.setAttribute("content", content);
  return el;
};

const removeSchemaById = (id) => {
  const el = document.head.querySelector(`script[data-schema-id="${id}"]`);
  if (el) el.remove();
};

const injectSchema = (id, obj) => {
  removeSchemaById(id);
  if (!obj) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute("data-schema-id", id);
  s.text = JSON.stringify(obj);
  document.head.appendChild(s);
};

/**
 * Comprehensive SEO helper.
 *
 * Props:
 *  - title, description, path         → title, meta description, canonical
 *  - canonical                        → override canonical (absolute or path)
 *  - robots                           → e.g. "noindex, follow"
 *  - ogImage                          → override OG/Twitter image (absolute URL)
 *  - ogType                           → "website" | "article" | ...
 *  - breadcrumbs: [{name, path}]      → BreadcrumbList JSON-LD (home auto-prepended)
 *  - article: { publishedTime, modifiedTime, author, image, section }
 *                                     → Article/BlogPosting JSON-LD (blogs)
 *  - service: { name, description, areaServed[] }
 *                                     → Service JSON-LD (e.g. delivery)
 *  - faq: [{ q, a }]                  → FAQPage JSON-LD (page-scoped)
 */
export const SEO = ({
  title,
  description,
  path = "",
  canonical,
  robots,
  ogImage,
  ogType = "website",
  breadcrumbs,
  article,
  service,
  faq,
}) => {
  useEffect(() => {
    // ---- title ----
    if (title) document.title = title;

    // ---- description ----
    if (description) {
      setOrCreate('meta[name="description"]', "meta", { name: "description" }, description);
    }

    // ---- canonical ----
    let canonHref;
    if (canonical) {
      canonHref = /^https?:/.test(canonical) ? canonical : `${SITE_ORIGIN}${canonical}`;
    } else {
      canonHref = `${SITE_ORIGIN}${path || window.location.pathname}`;
    }
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", canonHref);

    // ---- robots ----
    const robotsEl = document.head.querySelector('meta[name="robots"]');
    if (robots) {
      setOrCreate('meta[name="robots"]', "meta", { name: "robots" }, robots);
    } else if (robotsEl) {
      robotsEl.remove();
    }

    // ---- Open Graph ----
    const imageAbs = ogImage
      ? (/^https?:/.test(ogImage) ? ogImage : `${SITE_ORIGIN}${ogImage}`)
      : DEFAULT_OG_IMAGE;
    setOrCreate('meta[property="og:type"]', "meta", { property: "og:type" }, ogType);
    setOrCreate('meta[property="og:url"]', "meta", { property: "og:url" }, canonHref);
    if (title) setOrCreate('meta[property="og:title"]', "meta", { property: "og:title" }, title);
    if (description) setOrCreate('meta[property="og:description"]', "meta", { property: "og:description" }, description);
    setOrCreate('meta[property="og:image"]', "meta", { property: "og:image" }, imageAbs);
    setOrCreate('meta[property="og:image:secure_url"]', "meta", { property: "og:image:secure_url" }, imageAbs);

    // ---- Twitter Card ----
    setOrCreate('meta[name="twitter:card"]', "meta", { name: "twitter:card" }, "summary_large_image");
    if (title) setOrCreate('meta[name="twitter:title"]', "meta", { name: "twitter:title" }, title);
    if (description) setOrCreate('meta[name="twitter:description"]', "meta", { name: "twitter:description" }, description);
    setOrCreate('meta[name="twitter:image"]', "meta", { name: "twitter:image" }, imageAbs);

    // ---- BreadcrumbList schema ----
    if (breadcrumbs && breadcrumbs.length) {
      const items = [
        { name: "Home", path: "/" },
        ...breadcrumbs,
      ].map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: `${SITE_ORIGIN}${b.path}`,
      }));
      injectSchema("breadcrumbs", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
      });
    } else {
      removeSchemaById("breadcrumbs");
    }

    // ---- Article / BlogPosting schema ----
    if (article) {
      injectSchema("article", {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: title,
        description,
        image: article.image ? (/^https?:/.test(article.image) ? article.image : `${SITE_ORIGIN}${article.image}`) : imageAbs,
        author: {
          "@type": "Organization",
          name: article.author || "District Wellness Center",
          url: SITE_ORIGIN,
        },
        publisher: {
          "@type": "Organization",
          name: "District Wellness Center",
          logo: {
            "@type": "ImageObject",
            url: "https://customer-assets.emergentagent.com/job_4c37087a-7056-4639-a12f-74d5b630e91e/artifacts/xst6ipol_1-1920w.webp",
          },
        },
        datePublished: article.publishedTime,
        dateModified: article.modifiedTime || article.publishedTime,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": canonHref,
        },
        articleSection: article.section || "Cannabis Education",
      });
    } else {
      removeSchemaById("article");
    }

    // ---- Service schema ----
    if (service) {
      injectSchema("service", {
        "@context": "https://schema.org",
        "@type": "Service",
        name: service.name,
        description: service.description,
        provider: { "@id": `${SITE_ORIGIN}/#location-uptown` },
        areaServed: (service.areaServed || []).map((a) => ({ "@type": "Place", name: a })),
        serviceType: service.name,
        url: canonHref,
      });
    } else {
      removeSchemaById("service");
    }

    // ---- FAQ schema (page-scoped, cleaned up on unmount) ----
    if (faq && faq.length) {
      injectSchema("faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    } else {
      removeSchemaById("faq");
    }

    return () => {
      // Cleanup page-scoped schemas on unmount so SPA navigation doesn't duplicate.
      removeSchemaById("breadcrumbs");
      removeSchemaById("article");
      removeSchemaById("service");
      removeSchemaById("faq");
    };
  }, [
    title,
    description,
    path,
    canonical,
    robots,
    ogImage,
    ogType,
    // stringify avoids stale references from array/object props
    JSON.stringify(breadcrumbs || null),
    JSON.stringify(article || null),
    JSON.stringify(service || null),
    JSON.stringify(faq || null),
  ]);

  return null;
};

export default SEO;
